import type { NotificationRepository } from '../application/NotificationRepository'
import type {
  NotificationCategory,
  NotificationFilter,
  NotificationPreference,
  NotificationRow,
  NotificationScope,
  NotificationSummary,
} from '../domain/notification'

/**
 * Données de démonstration (sans Supabase : développement local, tests, captures). Toutes les références sont
 * fictives et marquées « Démo »; elles ne sont jamais servies quand une base est configurée.
 */
const minutesAgo = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString()

function row(
  values: Partial<NotificationRow> & Pick<NotificationRow, 'id' | 'type' | 'category' | 'priority'>,
): NotificationRow {
  const at = values.lastEventAt ?? minutesAgo(5)
  return {
    groupCount: 1,
    createdAt: at,
    lastEventAt: at,
    readAt: null,
    archivedAt: null,
    accessible: true,
    reference: null,
    data: {},
    destination: null,
    resolved: false,
    ...values,
  }
}

const demoId = (n: number) => `00000000-0000-4000-a000-${String(n).padStart(12, '0')}`

function seed(): NotificationRow[] {
  return [
    row({
      id: demoId(1),
      type: 'LEAD_CREATED',
      category: 'soumissions',
      priority: 'INFO',
      reference: 'Client Démo Gagnon · Saint-Jérôme',
      destination: `/leads/${demoId(101)}`,
      lastEventAt: minutesAgo(4),
    }),
    row({
      id: demoId(2),
      type: 'SMS_RECEIVED',
      category: 'alertes_sms',
      priority: 'INFO',
      groupCount: 3,
      reference: 'Client Démo Gagnon · 450 555-0142',
      destination: `/sms?onglet=reception&conversation=${demoId(102)}`,
      lastEventAt: minutesAgo(9),
    }),
    row({
      id: demoId(3),
      type: 'INVOICE_OVERDUE',
      category: 'facturation',
      priority: 'ALERT',
      reference: 'FAC-DEMO-1042 · Client Démo Tremblay',
      destination: `/invoices/${demoId(103)}`,
      lastEventAt: minutesAgo(12),
    }),
    row({
      id: demoId(4),
      type: 'MISSION_ENDED_UNRESOLVED',
      category: 'missions',
      priority: 'ALERT',
      reference: 'Mission 87 · Route Démo Nord',
      data: { unresolved: 2 },
      destination: `/missions/${demoId(104)}`,
      lastEventAt: minutesAgo(38),
    }),
    row({
      id: demoId(5),
      type: 'PAYMENT_RECORDED',
      category: 'facturation',
      priority: 'INFO',
      reference: 'FAC-DEMO-0997 · Client Démo Roy',
      destination: `/invoices/${demoId(105)}`,
      readAt: minutesAgo(30),
      lastEventAt: minutesAgo(80),
    }),
    row({
      id: demoId(6),
      type: 'CONTRACT_SIGNED',
      category: 'contrats',
      priority: 'INFO',
      reference: 'CTR-DEMO-0412 · Client Démo Lavoie',
      destination: `/contracts/${demoId(106)}`,
      readAt: minutesAgo(60),
      lastEventAt: minutesAgo(26 * 60),
    }),
    row({
      id: demoId(7),
      type: 'INVOICE_OVERDUE',
      category: 'facturation',
      priority: 'ALERT',
      reference: 'FAC-DEMO-0981 · Client Démo Pelletier',
      destination: `/invoices/${demoId(107)}`,
      resolved: true,
      readAt: minutesAgo(60),
      lastEventAt: minutesAgo(27 * 60),
    }),
    row({
      id: demoId(8),
      type: 'LEAD_CREATED',
      category: 'soumissions',
      priority: 'INFO',
      accessible: false,
      readAt: minutesAgo(60),
      lastEventAt: minutesAgo(6 * 24 * 60),
    }),
  ]
}

function matches(item: NotificationRow, scope: NotificationScope, now: number): boolean {
  if (scope.category && item.category !== scope.category) return false
  if (scope.priority && item.priority !== scope.priority) return false
  const age = now - new Date(item.lastEventAt).getTime()
  const day = 86_400_000
  if (scope.period === 'TODAY' && age > day) return false
  if (scope.period === '7D' && age > 7 * day) return false
  if (scope.period === '30D' && age > 30 * day) return false
  return true
}

export function createFixtureNotificationRepository(): NotificationRepository {
  let items = seed()
  let preferences: NotificationPreference[] = (
    [
      'soumissions',
      'contrats',
      'facturation',
      'courriels',
      'alertes_sms',
      'missions',
    ] as NotificationCategory[]
  ).map((category) => ({
    category,
    infoEnabled: category !== 'missions',
    alertEnabled: true,
    hasInfo: true,
    hasAlert: category !== 'courriels',
  }))

  const update = (ids: readonly string[], change: (item: NotificationRow) => NotificationRow | null) => {
    let count = 0
    items = items.map((item) => {
      if (!ids.includes(item.id)) return item
      const next = change(item)
      if (!next) return item
      count += 1
      return next
    })
    return count
  }

  return {
    getSummary(): Promise<NotificationSummary> {
      return Promise.resolve({
        unread: items.filter((item) => !item.readAt && !item.archivedAt).length,
        badges: { soumissions: 7, routes: 12, missions: 1, facturation: 23, courriels: 4, alertes_sms: 2 },
        routesSeason: '2026-2027',
        generatedAt: new Date().toISOString(),
      })
    },
    list(filter: NotificationFilter) {
      const now = Date.now()
      const scoped = items.filter((item) => matches(item, filter, now))
      const inTab = scoped.filter((item) =>
        filter.tab === 'ARCHIVED'
          ? item.archivedAt
          : filter.tab === 'UNREAD'
            ? !item.archivedAt && !item.readAt
            : !item.archivedAt,
      )
      return Promise.resolve({
        items: inTab.slice(filter.offset, filter.offset + filter.limit),
        total: inTab.length,
        counts: {
          all: scoped.filter((item) => !item.archivedAt).length,
          unread: scoped.filter((item) => !item.archivedAt && !item.readAt).length,
          archived: scoped.filter((item) => item.archivedAt).length,
        },
        limit: filter.limit,
        offset: filter.offset,
      })
    },
    setRead(ids, read) {
      const at = new Date().toISOString()
      return Promise.resolve(
        update(ids, (item) => (Boolean(item.readAt) === read ? null : { ...item, readAt: read ? at : null })),
      )
    },
    markAllRead(scope) {
      const now = Date.now()
      const ids = items
        .filter((item) => !item.readAt && !item.archivedAt && matches(item, scope, now))
        .map((item) => item.id)
      const at = new Date().toISOString()
      return Promise.resolve(update(ids, (item) => ({ ...item, readAt: at })))
    },
    setArchived(ids, archived) {
      const at = new Date().toISOString()
      return Promise.resolve(
        update(ids, (item) =>
          Boolean(item.archivedAt) === archived
            ? null
            : { ...item, archivedAt: archived ? at : null, readAt: item.readAt ?? at },
        ),
      )
    },
    getPreferences() {
      return Promise.resolve(preferences)
    },
    savePreferences(next) {
      preferences = preferences.map((current) => {
        const change = next.find((candidate) => candidate.category === current.category)
        return change
          ? { ...current, infoEnabled: change.infoEnabled, alertEnabled: change.alertEnabled }
          : current
      })
      return Promise.resolve(preferences)
    },
  }
}
